/**
 * fft_ocean — the Commons sea's FFT decision and the sea state → spectrum mapping (shader gaps R5, 2026-09-30). Pure.
 *
 * THE DECISION. The FFT sea draws when the tier asks for it (`fftOcean`, ultra), the backend is WebGPU (compute — read
 * by three's `isWebGPUBackend` flag, THE BACKEND FLAG LAW), no XR session presents (a headset is WebGL2 — VR_DECISIONS
 * turns the flag off too) and the kill switch is not thrown (`localStorage 'atlas:fft-ocean' = 'off'`). Everywhere else
 * the Gerstner sea draws, unchanged.
 *
 * THE SPECTRUM. The sea state keeps its meaning: `seaStateFor`'s amplitude scale multiplies the FFT sea exactly as it
 * multiplies the Gerstner set (the patch's energy at scale 1 IS the authored sea's), and the wind shapes the spectrum —
 * U10 grows with the wind (a calm's short chop, a gale's long sea), heading = the sea's own slewed heading (the primary
 * Gerstner wave turned by the sea state's rotation). The provider quantises (0.5 m/s, 5°) and cross-fades every change.
 */
import type { OceanSpectrumParams } from 'enginex/environment/water/fft/ocean_patch_math.js';

/** The patch Atlas runs (see the engine's ocean_patch_math for the laws behind each number). */
export const FFT_OCEAN = {
  /** 256² texels over 512 m — 2 m texels; the 8 m sea grid sits on the texel centres AND on a 64² vertex lattice. */
  resolution: 256,
  patchSize: 512,
  /** The spectrum-baked detail maps own every wave under 8 m (CHOPPY_PATCH_M) — never counted twice. */
  minWavelength: 8,
  /**
   * λ = 1.5 puts the FFT sea's Jacobian where the Gerstner sea's was (measured: J < 0.6 on 0.04 / 1.05 / 4.4 % of the
   * surface at amplitude 1 / 1.4 / 1.85 vs the Gerstner set's 0.01 / 0.78 / 3.7 %), so the sea state's whitecap
   * thresholds (`seaStateFor().foamJacobian`) carry over unchanged. λ = 1 made no whitecaps below a gale.
   */
  choppiness: 1.5,
  /** A sea-state change cross-fades over this long. */
  crossFadeS: 20,
  fetchKm: 60,
  /** The CPU query's pruning budget (off-lattice queries only; the drawn mesh's vertices come from the full lattice). */
  queryTailM: 0.002,
} as const;

export type FftOceanReason = 'on' | 'off-tier' | 'webgl2' | 'xr' | 'kill-switch' | 'error';

export function fftOceanDecision(
  preset: { fftOcean?: boolean },
  env: { webgpu: boolean; xrPresenting: boolean; killSwitch: boolean },
): { on: boolean; reason: FftOceanReason } {
  if (env.killSwitch) return { on: false, reason: 'kill-switch' };
  if (preset.fftOcean !== true) return { on: false, reason: 'off-tier' };
  if (env.xrPresenting) return { on: false, reason: 'xr' };
  if (!env.webgpu) return { on: false, reason: 'webgl2' };
  return { on: true, reason: 'on' };
}

/** `localStorage 'atlas:fft-ocean' = 'off'` (read safely — a blocked storage never throws out of the water build). */
export function fftOceanKilled(): boolean {
  try { return globalThis.localStorage?.getItem('atlas:fft-ocean') === 'off'; } catch { return false; }
}

/** U10 for the weather's wind strength (0..1) and a storm on top: 3 m/s in a dead calm, 8.4 at the typical 0.45, 15+ in a gale. */
export function fftWindSpeed(wind01: number, storm01 = 0): number {
  const w = Math.min(1, Math.max(0, Number.isFinite(wind01) ? wind01 : 0.45));
  const st = Math.min(1, Math.max(0, Number.isFinite(storm01) ? storm01 : 0));
  return 3 + 12 * w + 3 * st;
}

/** The spectrum the sea state asks for: the wind's speed, the sea's heading (radians, 0 = +X toward +Z). */
export function fftSpectrumFor(wind01: number, storm01: number, headingRad: number): OceanSpectrumParams {
  const deg = (headingRad * 180) / Math.PI;
  return { windSpeed: fftWindSpeed(wind01, storm01), windDirectionDeg: ((deg % 360) + 360) % 360, fetchKm: FFT_OCEAN.fetchKm, model: 'jonswap' };
}
